import { makeStyles } from "tss-react/mui"
import { useState } from "react"
import { faBell } from "@fortawesome/pro-light-svg-icons"
import { Box, IconButton, Menu, MenuItem, Stack, Tooltip, Typography } from "@mui/material"
import { useApiQuery } from "../../../../core/api/useApiQuery"
import { fetchNotifications, NotificationQuery } from "./notificationApi"
import { FontAwesomeSvgIcon } from "../NewsBellNavItem/NewsBellNavItem"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useDateTime } from "../../../../modules/localization/useDateTime"
import { LocalizedEnum, LocalizedStrict } from "../../../../modules/localization/components/AppLocalized"
import {
    MessageTagIcon,
    notificationOptions,
} from "../../../../modules/merchant/admin/messages/components/MessageTagAutoComplete"
import { faCircle } from "@fortawesome/pro-solid-svg-icons"

const useStyles = makeStyles()((theme) => ({
    iconButton: {
        color: theme.palette.tailwind.neutral[500],
    },
    menu: {
        "& .MuiPaper-root": {
            width: "30rem",
            padding: "1rem 1.5rem",
            borderRadius: ".5rem",
            boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
        },
    },
    menuItem: {
        padding: 0,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: ".5rem",
        "&:hover": {
            backgroundColor: "white",
        },
    },
    dateContainer: {
        display: "flex",
        alignItems: "center",
        gap: ".5rem",
        marginTop: ".25rem",
    },
    clockIcon: {
        color: theme.palette.grey[500],
        fontSize: "1rem",
    },
    counterBox: {
        background: "#2196F3",
        borderRadius: "6.25rem",
        color: theme.palette.common.white,
        padding: "0rem 0.406rem",
        fontSize: "0.7rem",
        lineHeight: "1rem",
        letterSpacing: "0.009rem",
        textAlign: "center",
        fontWeight: 500,
    },
    counter: {
        position: "absolute",
        top: "0rem",
        left: "1.75rem",
    },
    noNotifications: {
        padding: "1rem",
        alignItems: "center",
    },
    innerCard: {
        padding: "0.5rem",
        display: "flex",
        flexDirection: "column",
        gap: "0.625rem",
        width: "100%",
    },
    subject: {
        fontWeight: 500,
    },
    cardHeader: {
        display: "flex",
        alignItems: "center",
        gap: "0.625rem",
    },
    messageTime: {
        alignItems: "center",
        display: "flex",
        gap: "0.75rem",
    },
    colorBox: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flex: "none",
        width: "1.5rem",
        height: "1.5rem",
        borderRadius: "100%",
        color: theme.palette.common.white,
        "& > svg": {
            fontSize: ".75rem",
        },
    },
}))

type NotificationTopBarProps = Readonly<{}>

export function NotificationTopBarButton({}: NotificationTopBarProps) {
    const { classes, cx } = useStyles()
    const dateTimeFactory = useDateTime()

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const open = Boolean(anchorEl)

    const { data: notifications } = useApiQuery(fetchNotifications, {
        queryName: NotificationQuery.NOTIFICATIONS_LIST,
        dependencies: {},
        select: (data) => data.notifications,
        refetchInterval: 60000,
    })

    const totalAmount = notifications?.length ?? 0
    const isLargeAmount = totalAmount > 99

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget)
    }

    const handleClose = () => {
        setAnchorEl(null)
    }

    return (
        <>
            <LocalizedStrict id="notifications-tooltip-text" attrs={{ title: true }}>
                <Tooltip title="View notifications">
                    <IconButton onClick={handleClick} size="medium" className={cx(classes.iconButton)}>
                        <FontAwesomeSvgIcon icon={faBell} />
                        {!!totalAmount && (
                            <Box className={cx(classes.counter, classes.counterBox)}>
                                {isLargeAmount ? "99+" : totalAmount}
                            </Box>
                        )}
                    </IconButton>
                </Tooltip>
            </LocalizedStrict>

            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
                className={classes.menu}
            >
                <Stack spacing="1rem">
                    {notifications?.length ? (
                        <>
                            <Typography variant="h6">Notifications</Typography>
                            {notifications.map((notification) => {
                                const time = dateTimeFactory.fromApi(notification.created)
                                const selectedOption =
                                    notificationOptions.find((opt) => opt.value === notification.tag) ?? null

                                return (
                                    <MenuItem key={notification.id} className={classes.menuItem}>
                                        <Box className={classes.innerCard}>
                                            <Box className={classes.cardHeader}>
                                                {selectedOption && (
                                                    <MessageTagIcon
                                                        color={selectedOption.color}
                                                        icon={selectedOption.icon}
                                                        className={classes.colorBox}
                                                    />
                                                )}
                                                <Typography variant="body1" className={classes.subject}>
                                                    {notification.subject}
                                                </Typography>
                                            </Box>
                                            <Typography variant="body1" whiteSpace="normal">
                                                {notification.message}
                                            </Typography>
                                            <Box className={classes.messageTime}>
                                                {notification.tag && (
                                                    <>
                                                        <LocalizedEnum
                                                            base="user-home-top-bar-news-notification-time-type"
                                                            enumValue={notification.tag}
                                                        >
                                                            <Typography color="textSecondary" variant="caption">
                                                                Nyhed
                                                            </Typography>
                                                        </LocalizedEnum>
                                                        <FontAwesomeIcon icon={faCircle} color="#94A3B8" width="5px" />
                                                    </>
                                                )}
                                                <Typography variant="caption" color="textSecondary">
                                                    {time.toRelative({ style: "narrow" })}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </MenuItem>
                                )
                            })}
                        </>
                    ) : (
                        <Stack className={classes.noNotifications}>
                            <LocalizedStrict id="user-home-top-bar-news-no-notifications">
                                <Typography variant="subtitle2">No notifications right now</Typography>
                            </LocalizedStrict>
                            <LocalizedStrict id="user-home-top-bar-news-no-notifications-description">
                                <Typography textAlign="center" variant="body2" color="textSecondary">
                                    Here you will be able to read messages from the merchant and service center.
                                </Typography>
                            </LocalizedStrict>
                        </Stack>
                    )}
                </Stack>
            </Menu>
        </>
    )
}
